// React and PropTypes
import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";

// Third-party libraries
import _ from "lodash";
import classNames from "classnames";

// Toddle Design System components
import { SearchOutlined } from "@toddle-edu/ds-icons";
import {
  Checkbox,
  RadioButton,
  SearchInput,
  Tooltip,
} from "@toddle-edu/ds-web";

// Hooks
import { useI18n } from "Hooks";

// Styles
import classes from "./FilterOptions.scss";

// For static references
const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

/**
 * @component FilterOptions
 * @description Displays the options (checkboxes or radio buttons) for the currently selected filter category.
 * Includes search functionality and an "All" option for checkboxes if configured.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.filterOptionsWithApplyCount - The full array of filter category objects (used to find the current one).
 * @param {string|number} props.currentFilterType - The ID of the currently selected filter category.
 * @param {Function} props.handleChangeFilters - Callback to update the filters state in the parent.
 * @param {object} props.filters - The current state of all filters.
 * @param {string} props.testId - Base test ID for targeting elements.
 *
 * @example
 * const optionsWithCount = [
 *   { id: 'tags', label: 'Tags', optionsType: 'checkbox', showSearch: true, filterOptions: [{id: 'tag1', label: 'Tag 1'}, {id: 'tag2', label: 'Tag 2'}], filtersAppliedCount: 1 },
 *   // ... other categories
 * ];
 * const currentFilters = { tags: ['tag1'] };
 * const currentType = 'tags';
 * const handleChange = (newVal) => console.log('Changed tags:', newVal);
 *
 * <FilterOptions
 *   filterOptionsWithApplyCount={optionsWithCount}
 *   currentFilterType={currentType}
 *   handleChangeFilters={handleChange}
 *   filters={currentFilters}
 *   testId="my-filter"
 * />
 */
const FilterOptions = props => {
  const {
    filterOptionsWithApplyCount,
    currentFilterType,
    handleChangeFilters,
    filters,
    testId,
  } = props;
  const [search, setSearch] = useState("");
  const { t } = useI18n(["common"]);

  // Find the complete object for the current filter type
  const currentFilterCategory = useMemo(() => {
    return (
      _.find(filterOptionsWithApplyCount, { id: currentFilterType }) ||
      EMPTY_OBJECT
    );
  }, [currentFilterType, filterOptionsWithApplyCount]);

  // Destructure properties from the found category object
  const {
    label: currentFilterLabel = "",
    includeAllOption = false,
    showSearch = false,
    optionsType = "checkbox",
    filterOptions = EMPTY_ARRAY, // Default to empty array
  } = currentFilterCategory;

  // Get the current value for this specific filter type
  const currentFilterValue = useMemo(
    () =>
      filters[currentFilterType] ||
      (optionsType === "checkbox" ? EMPTY_ARRAY : null),
    [currentFilterType, filters, optionsType]
  );

  const isRadioOptionType = useMemo(() => optionsType === "radio", [
    optionsType,
  ]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!search) {
      return filterOptions; // Return all if no search term
    }
    return _.filter(filterOptions, option => {
      const { label } = option;
      return _.includes(label.toLowerCase(), search.toLowerCase());
    });
  }, [filterOptions, search]);

  const isAllCheckboxChecked = useMemo(() => {
    // Not applicable for radio or if 'All' option is disabled or no options shown
    if (isRadioOptionType || !includeAllOption || _.isEmpty(filteredOptions)) {
      return false;
    }

    // Get IDs of options currently visible after filtering
    const filteredOptionIds = _.map(filteredOptions, "id");
    // Check if the current value array contains all visible option IDs
    return _.difference(filteredOptionIds, currentFilterValue).length === 0;
  }, [
    isRadioOptionType,
    includeAllOption,
    filteredOptions,
    currentFilterValue,
  ]);

  // Check if "All" checkbox should be indeterminate
  const isAllCheckboxIndeterminate = useMemo(() => {
    // Only indeterminate if it's checkbox type, 'All' is included,
    // it's not fully checked, and there's at least one selection but not all.
    return (
      !isRadioOptionType &&
      includeAllOption &&
      !isAllCheckboxChecked &&
      !_.isEmpty(currentFilterValue) && // Some items are selected
      currentFilterValue.length < _.size(filteredOptions) // But not all visible items
    );
  }, [
    isRadioOptionType,
    includeAllOption,
    isAllCheckboxChecked,
    currentFilterValue,
    filteredOptions,
  ]);

  const handleAllCheckboxChange = useCallback(
    e => {
      if (isRadioOptionType || !includeAllOption) {
        return; // Should not happen if checkbox isn't rendered, but safeguard
      }

      // Select/deselect only the currently *filtered* options
      const filteredOptionIds = _.map(filteredOptions, "id");
      if (e.target.checked) {
        // Add filtered options to existing selections (handle potential duplicates)
        handleChangeFilters(_.union(currentFilterValue, filteredOptionIds));
      } else {
        // Remove filtered options from existing selections
        handleChangeFilters(
          _.difference(currentFilterValue, filteredOptionIds)
        );
      }
    },
    [
      filteredOptions,
      handleChangeFilters,
      includeAllOption,
      isRadioOptionType,
      currentFilterValue,
    ]
  );

  // Determine role and label for the options container
  const optionsContainerRole = isRadioOptionType ? "radiogroup" : "group";

  return (
    <div className={classes.container}>
      {showSearch ? (
        <div className={classes.searchWrapper}>
          <SearchInput
            value={search}
            onChange={setSearch}
            isDropdownSearch
            placeholder={t("common:search_placeholder")}
            testId={`${testId}-filterOptions`}
            // Keep workaround for dropdown closing issue
            onKeyUp={e => e.preventDefault()}
          />
        </div>
      ) : null}

      {/* Empty State */}
      {_.isEmpty(filteredOptions) ? (
        <div className={classes.emptyState}>
          <SearchOutlined variant="subtle" aria-hidden="true" />
          <span className={classNames("text-label", classes.textSecondary)}>
            {t("common:no_results")}
          </span>
        </div>
      ) : (
        // Options List
        <div
          className={classes.optionsContainer}
          role={optionsContainerRole}
          aria-label={currentFilterLabel}
          tabIndex={-1} // Keep tabIndex=-1 if container needs to be scrollable without being focusable itself
        >
          {/* "All" Checkbox */}
          {!isRadioOptionType && includeAllOption ? (
            <div className={classes.option}>
              <Checkbox
                isChecked={isAllCheckboxChecked}
                isIndeterminate={isAllCheckboxIndeterminate}
                onChange={handleAllCheckboxChange}
                testId={`${testId}-${currentFilterType}-all`}
              >
                <span className={classes.optionLabelCheckbox}>
                  {t("common:all")}
                </span>
              </Checkbox>
            </div>
          ) : null}

          {/* Individual Options */}
          {_.map(filteredOptions, option => {
            const { id, label } = option;

            const isSelected = isRadioOptionType
              ? currentFilterValue === id // Radio: check against single value
              : _.includes(currentFilterValue, id); // Checkbox: check if ID is in array

            return (
              <div className={classes.option} key={id}>
                {isRadioOptionType ? (
                  <RadioButton
                    size="medium"
                    onChange={e => {
                      handleChangeFilters(e.target.value);
                    }}
                    checked={isSelected}
                    value={id}
                    name={currentFilterType} // Group radios by filter type ID
                    testId={`${testId}-${currentFilterType}-${id}`}
                  >
                    <Tooltip tooltip={label}>
                      <span className={classes.optionLabel}>{label}</span>
                    </Tooltip>
                  </RadioButton>
                ) : (
                  <Checkbox
                    isChecked={isSelected}
                    onChange={() => {
                      // Toggle selection for checkboxes
                      handleChangeFilters(_.xor(currentFilterValue, [id]));
                    }}
                    testId={`${testId}-${currentFilterType}-${id}`}
                  >
                    <Tooltip tooltip={label}>
                      <span className={classes.optionLabelCheckbox}>
                        {label}
                      </span>
                    </Tooltip>
                  </Checkbox>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Define shape for filter options passed within categories
const filterOptionShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
});

// Define shape for the category object itself (as received in filterOptionsWithApplyCount)
const filterCategoryShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  optionsType: PropTypes.oneOf(["checkbox", "radio"]), // Default handled in FiltersOverlay
  filterOptions: PropTypes.arrayOf(filterOptionShape).isRequired,
  includeAllOption: PropTypes.bool, // Default handled in FiltersOverlay
  showSearch: PropTypes.bool, // Default handled in FiltersOverlay
  filtersAppliedCount: PropTypes.number, // Added by FiltersOverlay
});

FilterOptions.propTypes = {
  // Shape includes defaults defined in FiltersOverlay PropTypes
  filterOptionsWithApplyCount: PropTypes.arrayOf(filterCategoryShape)
    .isRequired,
  currentFilterType: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired, // Should always have a type selected here
  handleChangeFilters: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  testId: PropTypes.string.isRequired,
};

export default FilterOptions;
